import {
  addMonths,
  addWeeks,
  addYears,
  endOfMonth,
  endOfWeek,
  endOfYear,
  format,
  isSameMonth,
  isSameYear,
  parseISO,
  startOfMonth,
  startOfWeek,
  startOfYear,
} from 'date-fns';

export type PeriodMode = 'week' | 'month' | 'year' | 'custom';

export interface Period {
  mode: PeriodMode;
  /** Any date inside the period; the boundaries are derived from it. */
  anchor: Date;
  from: string;
  to: string;
  label: string;
}

export type WeekStart = 0 | 1;

/**
 * date-fns `format` works in local time, and entries are stored as local
 * calendar dates — so a Monday-start week in IST never bleeds into the
 * previous week the way a UTC round-trip would.
 */
export const toISODate = (d: Date): string => format(d, 'yyyy-MM-dd');

export const todayISO = (): string => toISODate(new Date());

export function buildPeriod(mode: PeriodMode, anchor: Date, weekStartsOn: WeekStart): Period {
  switch (mode) {
    case 'week': {
      const from = startOfWeek(anchor, { weekStartsOn });
      const to = endOfWeek(anchor, { weekStartsOn });
      const sameMonth = isSameMonth(from, to);
      const label = sameMonth
        ? `${format(from, 'd')} – ${format(to, 'd MMM yyyy')}`
        : `${format(from, 'd MMM')} – ${format(to, 'd MMM yyyy')}`;
      return { mode, anchor, from: toISODate(from), to: toISODate(to), label };
    }
    case 'month':
      return {
        mode,
        anchor,
        from: toISODate(startOfMonth(anchor)),
        to: toISODate(endOfMonth(anchor)),
        label: format(anchor, 'MMMM yyyy'),
      };
    case 'year':
      return {
        mode,
        anchor,
        from: toISODate(startOfYear(anchor)),
        to: toISODate(endOfYear(anchor)),
        label: format(anchor, 'yyyy'),
      };
    case 'custom':
      return { mode, anchor, from: toISODate(anchor), to: toISODate(anchor), label: 'Custom range' };
  }
}

export function customPeriod(from: string, to: string): Period {
  const a = parseISO(from);
  const b = parseISO(to);
  const label = isSameYear(a, b)
    ? `${format(a, 'd MMM')} – ${format(b, 'd MMM yyyy')}`
    : `${format(a, 'd MMM yyyy')} – ${format(b, 'd MMM yyyy')}`;
  return { mode: 'custom', anchor: a, from, to, label };
}

export function shiftPeriod(period: Period, step: -1 | 1, weekStartsOn: WeekStart): Period {
  switch (period.mode) {
    case 'week':
      return buildPeriod('week', addWeeks(period.anchor, step), weekStartsOn);
    case 'month':
      return buildPeriod('month', addMonths(period.anchor, step), weekStartsOn);
    case 'year':
      return buildPeriod('year', addYears(period.anchor, step), weekStartsOn);
    case 'custom':
      return period; // A hand-picked range has no meaningful "next".
  }
}

/** A year of days would be unreadable; a week of months would be empty. */
export function bucketFor(mode: PeriodMode): 'day' | 'month' {
  return mode === 'year' ? 'month' : 'day';
}
